// Backtest panel for the Monitor tab: runs POST /Backtests/run against the selected
// strategy, renders the full TradingView-Strategy-Tester-parity report, and offers a
// "Persist to strategy" button that fires PATCH /Strategies/{id}/backtest so subsequent
// webhook payloads include the rich metrics. Date range presets (Last 7/30/90/365 days,
// Custom, Entire history) drive the BacktestRequestDto FromDate/ToDate fields.
//
// When the backend returns warnings or empty results, this panel surfaces them as an
// actionable banner above the StrategyReport instead of silently rendering empty
// sub-components — so the user knows whether the issue is data availability, parameter
// thresholds, or strategy specificity.

import { useState } from "react";
import type { BacktestResponseDto, StrategyDto } from "../../api/types";
import { runBacktest } from "../../api/backtests";
import { patchStrategyBacktest } from "../../api/strategies";
import { StrategyReport } from "./StrategyReport";
import { selectPrimary } from "./primary";
import { DateRangePicker, ENTIRE_HISTORY, type DateRange } from "./DateRangePicker";

interface Props {
  strategy: StrategyDto | null;
  onResult: (result: BacktestResponseDto | null) => void;
}

export function BacktestPanel({ strategy, onResult }: Props) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BacktestResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [persisted, setPersisted] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(ENTIRE_HISTORY);

  if (!strategy) {
    return (
      <div style={panelStyle}>
        <h3 style={{ marginTop: 0 }}>Backtest</h3>
        <div style={{ color: "#8b949e", fontSize: 12 }}>Create a strategy first.</div>
      </div>
    );
  }

  const run = async () => {
    setBusy(true);
    setError(null);
    setPersisted(false);
    try {
      const r = await runBacktest({
        strategy: strategy.spec,
        direction: "both",
        minOccurrences: 5,
        fromDate: dateRange.from,
        toDate: dateRange.to,
      });
      setResult(r);
      onResult(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const persist = async () => {
    if (!result) return;
    try {
      // Persist the PRIMARY result block (netting → merged → scan), not the whole
      // BacktestResponseDto wrapper — AlertRouter reads score/winRate/suggestedSL/… at the
      // top level of LastBacktestJson, so the wrapper would silently zero every payload.
      await patchStrategyBacktest(strategy.id, selectPrimary(result) ?? result);
      setPersisted(true);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Categorise the response so we can pick the right banner. A "data-empty" result has no
  // trades on either side (and no merged) — the user is staring at an empty grid because
  // the engine had nothing to render, not because the components are broken.
  const hasAnyResult = !!(result && (result.strategy || result.long || result.short || result.merged));
  const isDataEmpty = result && !hasAnyResult;

  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Strategy Report</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            disabled={busy}
            interval={strategy.spec.interval}
          />
          <button onClick={run} disabled={busy} style={primaryButton}>{busy ? "Running…" : "Run backtest"}</button>
          {result && hasAnyResult && (
            <button onClick={persist} disabled={busy || persisted} style={button}>
              {persisted ? "Persisted ✓" : "Persist to strategy"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: "#f85149", fontSize: 12, padding: "4px 0", marginBottom: 8 }}>{error}</div>
      )}

      {/* Pre-run idle state */}
      {!result && !busy && (
        <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
          Click <strong>Run backtest</strong> to evaluate <code>{strategy.spec.coin} {strategy.spec.interval} {strategy.spec.marketType}</code> over {dateRange.label.toLowerCase()}.
        </div>
      )}

      {/* Loading state */}
      {busy && !result && (
        <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
          Running backtest over {dateRange.label.toLowerCase()}…
        </div>
      )}

      {/* Empty-data state (engine ran but produced nothing) — explain WHY and offer next moves. */}
      {isDataEmpty && (
        <EmptyDataBanner
          warnings={result.warnings ?? []}
          intervalLabel={strategy.spec.interval}
          coin={strategy.spec.coin}
          marketType={strategy.spec.marketType}
        />
      )}

      {/* Populated result — render the full report. Warnings still display as a yellow strip
          above the report (in StrategyReport itself) so the user sees soft caveats. */}
      {result && hasAnyResult && <StrategyReport result={result} />}
    </div>
  );
}

/**
 * Friendly "we ran the backtest but found nothing" banner. Categorises the warning text
 * coming back from the backend so the suggested fix matches the actual problem.
 */
function EmptyDataBanner({
  warnings,
  intervalLabel,
  coin,
  marketType,
}: {
  warnings: string[];
  intervalLabel: string;
  coin: string;
  marketType: string;
}) {
  // Distinguish "no candles fetched" from "candles fetched but no trades met threshold".
  const hasNoCandlesWarning = warnings.some((w) => w.toLowerCase().includes("no candles"));
  const hasZeroTradesWarning = warnings.some((w) => w.includes("Neither direction") || w.includes("MinOccurrences"));

  return (
    <div style={{
      padding: 14,
      background: "#3a2d1d",
      border: "1px solid #d29922",
      borderRadius: 6,
      fontSize: 12,
      color: "#f0d18a",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
        Backtest ran but produced no metrics
      </div>
      {hasNoCandlesWarning && (
        <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
          <strong>Why:</strong> Hyperliquid returned zero candles for the requested window.
          The exchange only stores the most recent 5,000 candles per (coin, interval), so for
          high-frequency intervals like <code>{intervalLabel}</code> the available history is
          short. The picked date window doesn't overlap with what Hyperliquid actually has.
          <div style={{ marginTop: 6 }}>
            <strong>Fix:</strong> use a shorter lookback (e.g. <em>Last 7 days</em>),
            switch to <em>Entire history</em> (fetches the latest 5,000 bars), or pick a
            higher timeframe like <em>5m</em>/<em>15m</em>/<em>1h</em> to cover more days
            within the same 5,000-bar wall.
          </div>
        </div>
      )}
      {hasZeroTradesWarning && (
        <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
          <strong>Why:</strong> the engine evaluated the candles but neither direction
          fired enough trade signals to clear the <code>MinOccurrences=5</code> threshold.
          The selected entry conditions almost never co-occur over this window
          ({coin} {intervalLabel} {marketType}).
          <div style={{ marginTop: 6 }}>
            <strong>Fix:</strong> pick conditions that fire more often (e.g. the{" "}
            <em>RSI momentum split</em> preset fires on every bar of trend), widen the date
            window, or use less restrictive thresholds (RSI &gt; 50 instead of RSI &gt; 70).
          </div>
        </div>
      )}
      {!hasNoCandlesWarning && !hasZeroTradesWarning && warnings.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
      {warnings.length === 0 && (
        <div style={{ lineHeight: 1.5 }}>
          The engine returned no data and no warning text — this is unexpected.
          Check the backend logs for <code>BacktestEngine</code> entries.
        </div>
      )}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  background: "#0d1117",
  border: "1px solid #30363d",
  borderRadius: 6,
  padding: 16,
};
const button: React.CSSProperties = {
  padding: "6px 12px",
  background: "#21262d",
  color: "#c9d1d9",
  border: "1px solid #30363d",
  borderRadius: 4,
  cursor: "pointer",
};
const primaryButton: React.CSSProperties = { ...button, background: "#238636", borderColor: "#2ea043" };
